import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { IconEdit } from '@tabler/icons-react';
import { PinInput } from '@mantine/core';
import { ErrorFallback, RESEND_OTP_TIMER } from 'src/utils/constants';
import { capitalizeFirstLetter } from 'src/utils';
import { showNotification } from '@mantine/notifications';
import { ReactComponent as IconError } from "src/assets/icons/toast-error.svg";
import { ReactComponent as IconSuccess } from "src/assets/icons/toast-success.svg";
import { useGetStep } from 'src/hooks';
import useCleverTapEvents from 'src/events/useGetEvents';
import { MOBILE_EVENT } from 'src/events/constant';
import {
  clearAccountError, setAccountStatus, resendOtp, verifyOTP,
} from './accountSlice';
import { RootState } from '../../redux/store';
import { ScreenWrapper } from '../../components';
import { useStyles } from './signupVerification.styles';
import { Button } from '../../components/atoms';

export const SignupVerification = () : JSX.Element => {
  const { classes, cx } = useStyles();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { onContinueAcOpening } = useGetStep()
  const {
    verifyToken, mobileNumber, authToken, isOtpResent,
  } = useSelector((state:RootState) => state.account);
  const accountState = useSelector((state:RootState) => state.account);

  const [otp, setOtp] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [timer, setTimer] = useState(RESEND_OTP_TIMER);
  const isButtonDisabled = otp.length !== 4
  const { TriggerEvent } = useCleverTapEvents();

  const onNextClick = () => {
    navigate('/');
  };

  const onEditNumber = () : void => {
    navigate('/login', { replace: true });
  };

  const handleOtpChange = (val?: any) : void => {
    if (val) {
      setOtp(val.toString());
    } else setOtp('')
  };

  const handleSubmitOtp = () : void => {
    dispatch(
      verifyOTP({
        otp,
        verifyToken,
        mobileNumber,
      })
    );
    TriggerEvent(MOBILE_EVENT.mobileOtp, { errorMessage: errorMessage || "" })
  };

  const handleRoute = () => {
    const step = onContinueAcOpening();
    navigate(step);
  }

  const handleResendOtp = ():void => {
    dispatch(resendOtp({
      mobileNumber,
      verifyToken,
    }));
    setOtp('');
    setTimer(RESEND_OTP_TIMER);
    setErrorMessage('');
    TriggerEvent(MOBILE_EVENT.resendOtp, { typeOfResend: "mobile", errorMessage });
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setTimer((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    if (authToken) {
      navigate('/', { replace: true });
    } else if (verifyToken === null) {
      navigate('/login', { replace: true });
    }
    return () => {
      clearInterval(interval);
    };
  }, []);

  // OTP Resend
  useEffect(() => {
    if (isOtpResent && accountState.status === 'success') {
      showNotification({
        color: 'green',
        message: 'OTP resent successfully!',
        icon: <IconSuccess />,
      });
      dispatch(setAccountStatus('idle'));
    } else if (isOtpResent === false && accountState.status === 'error') {
      const errMessage = capitalizeFirstLetter(accountState.error.message) || ErrorFallback;
      showNotification({
        color: 'red',
        message: errMessage,
        icon: <IconError />,
      });
      setErrorMessage(errMessage);
      dispatch(clearAccountError());
    }
  }, [isOtpResent]);

  // OTP Verify
  useEffect(() => {
    if (accountState.isOtpVerified) {
      handleRoute();
      showNotification({
        color: 'green',
        message: 'Log in Successful!',
        icon: <IconSuccess />,
      });
    } else if (accountState.isOtpVerified === false && accountState.status === 'error') {
      const errMessage = capitalizeFirstLetter(accountState.error.message) || ErrorFallback;
      showNotification({
        color: 'red',
        message: errMessage,
        icon: <IconError />,
      });
      setErrorMessage(errMessage);
      handleOtpChange()
      dispatch(clearAccountError());
    }
  }, [accountState.isOtpVerified, accountState.status]);

  return (
    <ScreenWrapper>
      <div className="csMainContainer">
        <div className={classes.contentContainer}>
          <h2 className="csHeading">Mobile verification</h2>
          <div className={classes.descContainer}>
            <p className="csDescription">{"Enter the OTP sent to "}</p>
            <button
              type="button"
              onClick={onEditNumber}
              className={classes.changeNumberLink}
            >
              {` +91-${mobileNumber}`}
              <IconEdit size={18} />
            </button>
          </div>
          <div>
            <PinInput
              className={classes.otpInputStyledWrapper}
              classNames={{
                wrapper: classes.pinInputWrapper,
              }}
              oneTimeCode
              onChange={handleOtpChange}
              autoFocus
              value={otp}
              inputMode="numeric"
              error={!!errorMessage}
              length={4}
              onComplete={handleSubmitOtp}
              type="number"
            />
          </div>
          {errorMessage && (
            <p className={cx(classes.inputDescription, classes.inputError)}>
              {errorMessage}
            </p>
          )}
          <p className={classes.inputDescription}>
            Having trouble?
            {" "}
            {timer > 0 ? (
              `Request a new OTP in 00:${timer < 10 ? `0${timer}` : timer}`
            ) : (
              <button
                type="button"
                name="resend otp"
                className={classes.resend}
                onClick={handleResendOtp}
              >
                Resend OTP
              </button>
            )}
          </p>
        </div>
        <div className="csFooter">
          <Button
            disabled={isButtonDisabled}
            onClick={onNextClick}
            loading={accountState.status === "loading"}
          >
            Next
          </Button>
        </div>
      </div>
    </ScreenWrapper>
  );
};

export default SignupVerification;
